import { WarningRounded } from "@mui/icons-material";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useContext, useState } from "react";
import {useFormContext, useWatch} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { AxiosError } from "axios";
import CustomLoader from "../../../../../../../util/component/CustomLoader";
import { setSnackbar } from "../../../../../../../util/state/globalAction";
import { GlobalContext } from "../../../../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../../../../util/state/globalVariables";
import { book, checkStopSell } from "../../../../../api/services";
import DialogCloseButton from "../../../../../components/DialogCloseButton";
import useDialog from "../../../../../hooks/useDialog";
import { LOCALE_DATE_FORMAT } from "../../../../../utils";
import { bookingIdState, isPersonBAState } from "../../../recoil";
import { IFormValues } from "../../../types";
import { BookingStatus, RentalType } from "../../../../../types";
import ConfirmDialog from "../confirm-dialog/ConfirmDialog";

const ButtonBook = () => {

    const { t } = useTranslation('booking');

    const { dispatchGlobalData } = useContext(GlobalContext);

    const navigate = useNavigate();

    const bookingId = useRecoilValue(bookingIdState);
    const isPersonBA = useRecoilValue(isPersonBAState);

    const formContext = useFormContext<IFormValues>();

    const tenant = useWatch({ control: formContext.control, name: "basicDetails.tenants" });
    const dealer = useWatch({ control: formContext.control, name: "basicDetails.dealers" });

    const confirmBook = useCallback(async () => {
        const data = formContext.getValues();
        if (
            !data.basicDetails.pickupLocation ||
            !data.basicDetails.dropoffLocation ||
            !data.basicDetails.carGroup ||
            !bookingId
        ) {
            return;
        }
        
        await bookMutation.mutateAsync({
            id: bookingId,
            confirmData: {
                firstDriver: {
                    customerId: data.firstDriver.id,
                    name: data.firstDriver.name,
                    surname: data.firstDriver.surname,
                    birthDate: data.firstDriver.birthDate ? data.firstDriver.birthDate.format(LOCALE_DATE_FORMAT) : '',
                    birthNation: data.firstDriver.birthNation ?? '',
                    birthPlace: data.firstDriver.birthPlace ?? '',
                    residenceCountry: data.firstDriver.residenceCountry ?? '',
                    fiscalCode: data.firstDriver.fiscalCode,
                    drivingLicenseNumber: data.firstDriver.drivingLicenseNumber,
                    email: data.firstDriver.email,
                    mobile: data.firstDriver.mobile,
                    mobilePrefix: data.firstDriver.mobilePrefix || null,
                    gender: data.firstDriver.gender  ?? ''
                },
                bookingOwner:  data.basicDetails.rentalType === RentalType.RETAIL || isPersonBA ?
                    {
                        customerId: data.bookingOwner.id,
                        name: data.bookingOwner.name,
                        surname: data.bookingOwner.surname,
                        birthDate: data.bookingOwner.birthDate ? data.bookingOwner.birthDate.format(LOCALE_DATE_FORMAT) : '',
                        birthNation: data.bookingOwner.birthNation ?? '',
                        birthPlace: data.bookingOwner.birthPlace ?? '',
                        residenceCountry: data.bookingOwner.residenceCountry ?? '',
                        fiscalCode: data.bookingOwner.fiscalCode,
                        drivingLicenseNumber: data.bookingOwner.drivingLicenseNumber,
                        email: data.bookingOwner.email,
                        mobile: data.bookingOwner.mobile,
                        mobilePrefix: data.bookingOwner.mobilePrefix || null
                    } : null,
                bookingCompanyOwner: data.basicDetails.rentalType !== RentalType.RETAIL && !isPersonBA ?
                    {
                        customerId: data.bookingOwner.id,
                        companyName: data.bookingOwner.companyName,
                        country: data.bookingOwner.country,
                        city: data.bookingOwner.city,
                        streetName: data.bookingOwner.streetName,
                        streetNumber: data.bookingOwner.streetNumber,
                        postalCode: data.bookingOwner.postalCode,
                        email: data.bookingOwner.email,
                        pec: data.bookingOwner.certifiedEmail,
                        telephone: data.bookingOwner.telephone,
                        mobile: data.bookingOwner.mobile,
                        mobilePrefix: data.bookingOwner.mobilePrefix || null,
                        vatNumber: data.bookingOwner.vatNumber,
                        vatNumberCountry: data.bookingOwner.vatNumberCountry,
                        fiscalCode: data.bookingOwner.fiscalCode,
                        customerCode: data.basicDetails.customerCode ?? '',
                        sdiCode: data.bookingOwner.invoiceCode,
                        sapId: '',
                        legalEntity: data.bookingOwner.legalEntity,
                        publicAuthority: data.bookingOwner.publicAuthority ? '1' : '0',
                        paIdNumber: data.bookingOwner.publicAuthority ? data.bookingOwner.paIdNumber : '',
                        siren: data.bookingOwner.siren,
                        siret: data.bookingOwner.siret,
                    } : null,
                invoice: {
                    customerId: data.invoice.id,
                    type: data.invoice.type,
                    name: data.invoice.name,
                    surname: data.invoice.surname,
                    birthDate: data.invoice.birthDate ? data.invoice.birthDate.format(LOCALE_DATE_FORMAT) : '',
                    birthNation: data.invoice.birthNation ?? '',
                    birthPlace: data.invoice.birthPlace ?? '',
                    residenceCountry: data.invoice.residenceCountry ?? '',
                    fiscalCode: data.invoice.fiscalCode,
                    email: data.invoice.email,
                    mobile: data.invoice.mobile,
                    drivingLicenseNumber: data.invoice.drivingLicenseNumber,
                    billingCountry: data.invoice.billingCountry ?? '',
                    billingCity: data.invoice.billingCity ?? '',
                    billingStreetName: data.invoice.billingStreetName,
                    billingStreetNumber: data.invoice.billingStreetNumber,
                    billingPostalCode: data.invoice.billingPostalCode,
                    pec: data.invoice.certifiedEmail,
                    vatNumber: data.invoice.vatNumber,
                    vatNumberCountry: data.invoice.vatNumberCountry,
                    invoiceCode: data.invoice.invoiceCode,
                    companyName: data.invoice.companyName,
                    mobilePrefix: data.invoice.mobilePrefix || null,
                    billingProvince: data.invoice.billingProvince ?? '',
                    legalEntity: data.invoice.legalEntity,
                    publicAuthority: data.invoice.publicAuthority ? '1' : '0',
                    paIdNumber: data.invoice.publicAuthority ? data.invoice.paIdNumber : '',
                    siren: data.invoice.siren || '',
                    siret: data.invoice.siret || '',
                },
                additionalDrivers: data.additionalDrivers.map(ad => ({
                    customerId: ad.id,
                    name: ad.name,
                    surname: ad.surname,
                    email: ad.email
                })),
            },
            tenant: tenant,
            dealer: dealer
        });
    }, [bookingId, isPersonBA]);

    const bookMutation = useMutation(
        book,
        {
            onSuccess: (data) => {
                if (data.blackList) {
                    confirmationDialog.handleClose();
                    blacklistErrorDialog.handleShow();
                    return;
                }
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: (
                                t('message.info.new_booking') +
                                t(`status.${data.status}`)) +
                                (data.subStatus ? ` - ${t(`substatus.${data.subStatus}`)}` : '')
                            ,
                            type: data.status === BookingStatus.pending ? snackbarTypes.warning : snackbarTypes.info,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
                navigate(`/booking/view/${data.id}`);
            },
            onError: (err) => {
                let message = '';

                if (err instanceof (AxiosError)) {
                    switch (err.response?.data.code) {
                        case '1':
                            message = t('message.error.main_driver_fiscal_code');
                            formContext.setError(
                                'firstDriver.fiscalCode',
                                {
                                    message: 'message.validation.invalid_fiscal_code'
                                }
                            )
                            break;
                        case '2':
                            message = t('message.error.billing_owner_fiscal_code');
                            formContext.setError(
                                'invoice.fiscalCode',
                                {
                                    message: 'message.validation.invalid_fiscal_code'
                                }
                            )
                            break;
                        case '3':
                            message = t('message.error.booking_owner_fiscal_code');
                            formContext.setError(
                                'bookingOwner.fiscalCode',
                                {
                                    message: 'message.validation.invalid_fiscal_code'
                                }
                            )
                            break;
                        case '4':
                            message = t('message.error.discount_code');

                            formContext.setError(
                                'extraServices.discountCode',
                                {
                                    message: 'message.validation.invalid_discount_code'
                                }
                            )
                            break;
                        case '400':
                            message = t('message.error.suspended_customers');
                            break;
                        default:
                            message = t('message.error.default');
                    }
                }
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: message,
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        }
    );
    const checkStopSellMutation = useMutation(
        checkStopSell,
        {
            onSuccess: async (data) => {
                confirmationDialog.handleClose();
                if (!data.stopSell && !data.stopOverbooking) {
                    confirmBook();
                }
                else {
                    if (data.ignore || data.ignore === null) {
                        if (data.stopSell && data.stopOverbooking) {
                            setStopDialogMessage(t('message.warning.stop_sell_overbooking'));
                        }
                        else if (data.stopSell) {
                            setStopDialogMessage(t('message.warning.stop_sell'));
                        }
                        else {
                            setStopDialogMessage(t('message.warning.stop_overbooking'));
                        }
                        stopSellWarningDialog.handleShow();
                    }
                    else {
                        if (data.stopSell && data.stopOverbooking) {
                            setStopDialogMessage(t('message.error.stop_sell_overbooking'));
                        }
                        else if (data.stopSell) {
                            setStopDialogMessage(t('message.error.stop_sell'));
                        }
                        else {
                            setStopDialogMessage(t('message.error.stop_overbooking'));
                        }
                        stopSellErrorDialog.handleShow();
                    }
                }
            },
            onError: () => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        }
    )

    const confirmationDialog = useDialog();
    const blacklistErrorDialog = useDialog();
    const stopSellErrorDialog = useDialog();
    const stopSellWarningDialog = useDialog();

    const [blacklistDialogDisabled, setBlacklistDialogDisabled] = useState<boolean>(false);
    const [stopDialogMessage, setStopDialogMessage] = useState<string>('');

    const handleSave = useCallback(async () => {
        if (!bookingId) {
            return;
        }
        checkStopSellMutation.mutate({
            bookingId
        })
    }, [bookingId]);
    const onSubmit = useCallback(() => {
        confirmationDialog.handleShow();
    }, []);
    const handleBlacklistDialogClose = useCallback(() => {
        setBlacklistDialogDisabled(true);
        navigate(0);
    }, []);

    return (
        <>
            <Button onClick={formContext.handleSubmit(onSubmit)}>
                {t('label.book')}
            </Button>
            <ConfirmDialog
                title={t('label.book')}
                open={confirmationDialog.open}
                onClose={confirmationDialog.handleClose}
                onConfirm={handleSave}
            />
            <Dialog
                open={blacklistErrorDialog.open}
                fullWidth
                maxWidth="sm"
            >
                <DialogCloseButton
                    onClick={handleBlacklistDialogClose}
                    disabled={blacklistDialogDisabled}
                />
                <DialogTitle>
                    <Stack
                        direction="row"
                        alignItems="center"
                        columnGap={1}
                    >
                        <WarningRounded color="error" />
                        {t('label.error')}
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        sx={{ whiteSpace: 'pre-line' }}
                    >
                        {t('message.error.insert_booking')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleBlacklistDialogClose}
                        disabled={blacklistDialogDisabled}
                    >
                        {t('label.close')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={stopSellErrorDialog.open}
                onClose={stopSellErrorDialog.handleClose}
                fullWidth
                maxWidth="sm"
            >
                <DialogCloseButton
                    onClick={stopSellErrorDialog.handleClose}
                />
                <DialogTitle>
                    <Stack
                        direction="row"
                        alignItems="center"
                        columnGap={1}
                    >
                        <WarningRounded color="error" />
                        {t('label.error')}
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        sx={{
                            whiteSpace: 'pre-line'
                        }}
                    >
                        {stopDialogMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={stopSellErrorDialog.handleClose}
                    >
                        {t('label.ok')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={stopSellWarningDialog.open}
                onClose={stopSellWarningDialog.handleClose}
                fullWidth
                maxWidth="sm"
            >
                <DialogCloseButton
                    onClick={stopSellWarningDialog.handleClose}
                />
                <DialogTitle>
                    <Stack
                        direction="row"
                        alignItems="center"
                        columnGap={1}
                    >
                        <WarningRounded color="warning" />
                        {t('label.warning')}
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        sx={{
                            whiteSpace: 'pre-line'
                        }}
                    >
                        {stopDialogMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={stopSellWarningDialog.handleClose}
                    >
                        {t('label.back')}
                    </Button>
                    <Button
                        onClick={confirmBook}
                    >
                        {t('label.save')}
                    </Button>
                </DialogActions>
            </Dialog>
            <CustomLoader
                loading={
                    checkStopSellMutation.isLoading ||
                    bookMutation.isLoading
                }
            />
        </>
    );
};

export default ButtonBook;
